// What a press on the voice key or button means (spec "Names, voice gestures and
// reattaching"): the rule the iPhone and the Mac use (ios/CMX/Core/VoiceGesture.swift), with
// the same thresholds; both are checked against voice-gestures.json.
//
// The keys that start it (Settings › Voice): the Mac holds fn to talk and presses fn Space for
// hands-free (`keys` in voice-gestures.json). A browser can't see fn, so here each is a chord
// the person picks: Alt Space to hold, Alt Shift Space for hands-free.
//
// How long the press lasted comes from the input events' own timestamps (event.timeStamp),
// never the clock when the page gets round to them.

import rule from './voice-gestures.json' with { type: 'json' };

export type Release = 'handsFree' | 'insert' | 'cancel';

export const TAP_SECONDS: number = rule.tapSeconds;
export const SLOP: number = rule.slop;
export const CANCEL_AT: number = rule.cancelAt;
export const LOCK_AT: number = rule.lockAt;

/** While held: far enough left to cancel on release, or up at the lock. */
export function held(dx: number, dy: number): { cancelling: boolean; lock: boolean } {
  const cancelling = dx < CANCEL_AT;
  return { cancelling, lock: dy < LOCK_AT && !cancelling };
}

/** Let go after `seconds` (release event time minus press event time), moved by dx, dy;
 *  `starting`: the microphone isn't listening yet. A tap, or a release before the microphone
 *  came up, goes on hands-free; a hold inserts; slid left cancels. */
export function release(seconds: number, dx: number, dy: number, starting: boolean): Release {
  if (held(dx, dy).cancelling) return 'cancel';
  const tap = seconds < TAP_SECONDS && Math.abs(dx) < SLOP && Math.abs(dy) < SLOP;
  return tap || starting ? 'handsFree' : 'insert';
}

/** A key and the modifiers held with it (`code` is KeyboardEvent.code, so it ignores layout and Option's characters). */
export interface Shortcut {
  code: string;
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
}

export interface VoiceShortcuts {
  /** Hold to talk; let go to insert (a tap goes on hands-free, as the button's). */
  push: Shortcut;
  /** Press to start listening with nothing held; press again to insert. Esc cancels. */
  handsFree: Shortcut;
}

/** What a browser can offer in place of fn: Alt Space to hold, Alt Shift Space for hands-free. */
export const DEFAULT_SHORTCUTS: VoiceShortcuts = {
  push: { code: 'Space', alt: true },
  handsFree: { code: 'Space', alt: true, shift: true },
};

/** The key and exactly its modifiers: Alt Space doesn't match Alt Shift Space. */
export function matches(e: { code: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }, s: Shortcut): boolean {
  return e.code === s.code && e.altKey === !!s.alt && e.ctrlKey === !!s.ctrl && e.metaKey === !!s.meta && e.shiftKey === !!s.shift;
}

export const same = (a: Shortcut, b: Shortcut) =>
  a.code === b.code && !!a.alt === !!b.alt && !!a.ctrl === !!b.ctrl && !!a.meta === !!b.meta && !!a.shift === !!b.shift;

/** Letting go of the shortcut's key or one of its modifiers ends a hold. */
export function releases(e: { code: string; key: string }, s: Shortcut): boolean {
  return (
    e.code === s.code ||
    (e.key === 'Alt' && !!s.alt) ||
    (e.key === 'Control' && !!s.ctrl) ||
    (e.key === 'Meta' && !!s.meta) ||
    (e.key === 'Shift' && !!s.shift)
  );
}

export const isModifierCode = (code: string) => /^(Alt|Control|Meta|Shift|OS)(Left|Right)$/.test(code);

/** A chord worth keeping: a plain letter would eat typing, so it needs Alt, Ctrl or Meta (F keys stand alone). */
export const usable = (s: Shortcut) => !isModifierCode(s.code) && (!!s.alt || !!s.ctrl || !!s.meta || /^F\d+$/.test(s.code));

const keyName = (code: string) => code.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Arrow/, '');

/** "⌥⇧ Space" on a Mac, "Alt+Shift+Space" elsewhere. */
export function label(s: Shortcut, mac: boolean): string {
  const k = keyName(s.code);
  if (mac) return `${s.ctrl ? '⌃' : ''}${s.alt ? '⌥' : ''}${s.shift ? '⇧' : ''}${s.meta ? '⌘' : ''} ${k}`.trim();
  return [s.ctrl && 'Ctrl', s.alt && 'Alt', s.shift && 'Shift', s.meta && 'Meta', k].filter(Boolean).join('+');
}

/* ---------- Settings › Voice: the bindings each voice action has (as Wispr Flow's Shortcuts) ---------- */

/** A modifier by itself: `KeyboardEvent.key`'s name for it. */
export type Mod = 'Control' | 'Alt' | 'Meta' | 'Shift';

/** One way to start: a chord; a modifier held by itself (Push to talk); a modifier tapped twice (Hands-free). */
export type Binding = ({ kind: 'chord' } & Shortcut) | { kind: 'hold'; mod: Mod } | { kind: 'double'; mod: Mod };

export interface VoiceBindings {
  push: Binding[];
  handsFree: Binding[];
}

/** Each action keeps one to three. */
export const MAX_BINDINGS = 3;
/** A held modifier talks only once it has been down this long with no other key (so Ctrl C stays Ctrl C). */
export const HOLD_ARM_MS = 250;
/** Two taps of a modifier this close are a double tap. */
export const DOUBLE_TAP_MS = 300;

export const DEFAULT_BINDINGS: VoiceBindings = {
  push: [{ kind: 'chord', ...DEFAULT_SHORTCUTS.push }],
  handsFree: [{ kind: 'chord', ...DEFAULT_SHORTCUTS.handsFree }],
};

export const chordOf = (b: Binding): Shortcut | null => (b.kind === 'chord' ? b : null);

export function sameBinding(a: Binding, b: Binding): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'chord' && b.kind === 'chord') return same(a, b);
  return (a as { mod: Mod }).mod === (b as { mod: Mod }).mod;
}

const MOD_CAP: Record<Mod, { mac: string; other: string }> = {
  Control: { mac: '⌃ Control', other: 'Ctrl' },
  Alt: { mac: '⌥ Option', other: 'Alt' },
  Meta: { mac: '⌘ Command', other: 'Meta' },
  Shift: { mac: '⇧ Shift', other: 'Shift' },
};

/** The key caps a binding shows, one per key: ["⌥ Option", "Space"]; a double tap's caps follow "Double tap". */
export function caps(b: Binding, mac: boolean): string[] {
  if (b.kind !== 'chord') return [MOD_CAP[b.mod][mac ? 'mac' : 'other']];
  const mods: Mod[] = (['Control', 'Alt', 'Shift', 'Meta'] as const).filter((m) => (m === 'Control' ? b.ctrl : m === 'Alt' ? b.alt : m === 'Shift' ? b.shift : b.meta));
  const key = keyName(b.code);
  return [...mods.map((m) => (mac ? MOD_CAP[m].mac.split(' ')[0] : MOD_CAP[m].other)), key];
}

/** One line for a tooltip: "⌥ Space", "Ctrl" (held), "double tap Ctrl". */
export function bindingLabel(b: Binding, mac: boolean): string {
  if (b.kind === 'chord') return label(b, mac);
  const k = mac ? MOD_CAP[b.mod].mac.split(' ')[0] : MOD_CAP[b.mod].other;
  return b.kind === 'double' ? `double tap ${k}` : k;
}

/** What cmx itself does with a chord, if anything (Workspace.tsx's shortcuts), to say why it can't be a voice key. */
export function reservedBy(s: Shortcut, mac: boolean): string | null {
  const cmd = mac ? !!s.meta && !s.ctrl && !s.alt : !!s.ctrl && !s.meta && !s.alt;
  const k = keyName(s.code);
  const name = mac ? `⌘${k}` : `Ctrl+${k}`;
  if (cmd && !s.shift && s.code === 'KeyK') return `cmx uses ${name} for the command palette.`;
  if (cmd && ['Equal', 'Minus', 'Digit0', 'NumpadAdd', 'NumpadSubtract', 'Numpad0'].includes(s.code)) return `cmx uses ${name} to zoom the terminal.`;
  if (mac && cmd && !s.shift && s.code === 'KeyB') return 'cmx uses ⌘B to show or hide the sidebar.';
  const num = mac ? !!s.meta && !s.ctrl && !s.alt : !!s.alt && !s.ctrl && !s.meta;
  if (num && !s.shift && /^Digit[1-9]$/.test(s.code)) return `cmx uses ${mac ? '⌘' : 'Alt+'}${k} to open row ${k}.`;
  return null;
}
